import Loadable from 'react-loadable';
import moment, { Moment } from 'moment';
import { Loader } from '@rootCommon/components';
import {
  cfAccessClientId, cfAccessClientSecret, DATE_FORMAT, isDevelopment, TIME_FORMAT,
} from '@rootCommon/constants/common';
import { GlossaryType, IdType } from '@rootCommon/types/common';
import axios, { AxiosError } from 'axios';
import { RequestConfig } from '@rootCommon/services/http';

export enum RepoURL {
  DASHBOARD = 'dashboard',
  ADMIN = 'admin',
}

export const addCFClientAccessHeaders = (value: RequestConfig): void => {
  if (isDevelopment) {
    const extraHeaders = {
      'CF-Access-Client-Id': cfAccessClientId,
      'CF-Access-Client-Secret': cfAccessClientSecret,
    };
    // eslint-disable-next-line no-param-reassign
    value.headers = { ...value.headers, ...extraHeaders };
  }
};

export const getAbsolutePath = (path: string): string => process.env.PUBLIC_URL + path;

export const getApplicationRepoURL = (app: RepoURL): string | undefined => (
  isDevelopment
    ? process.env.REACT_APP_URL
    // eslint-disable-next-line no-useless-escape
    : `${process.env.PUBLIC_URL.replace(/[^\/]*$/, '')}${app}`
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export const dynamicImport = (lf: () => Promise<any>) => Loadable({
  loader: lf,
  loading: Loader,
});

export const formatDate = (date: string, format?: string): string => moment(date).format(format || DATE_FORMAT);

export const getTimeFromDate = (date: string, format?: string): string => moment(date).format(format || TIME_FORMAT);

export const getTimeRange = (dateFrom: string, dateTo: string): string => (
  `${getTimeFromDate(dateFrom)} - ${getTimeFromDate(dateTo)}`
);

export const getUtcOffset = (): number => moment().utcOffset() / 60; // get UTC offset in hours

export const isToday = (date: string | Moment): boolean => moment(date).isSame(moment(), 'day');

export const setDatePickerRange = (payload: {
  current: moment.Moment | null;
  from?: moment.Moment;
  to?: moment.Moment;
}): boolean => {
  const { current, from, to } = payload;

  return (current && from && current < from.startOf('day')) || (current && to && current > to.endOf('day')) || false;
};

export const getGlossaryItemName = <T extends GlossaryType[]>(glossary: T, id: IdType): string | undefined => (
  glossary.find((item) => item.id === id)?.name
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export const isEqual = (x: any, y: any): boolean => {
  const ok = Object.keys;
  const tx = typeof x;
  const ty = typeof y;

  return x && y && tx === 'object' && tx === ty
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    ? ok(x).length === ok(y).length && ok(x).every((key) => isEqual(x[key], y[key]))
    : x === y;
};

export type AxiosErrorData = {
  error?: string;
  message?: string;
  statusCode?: number;
};

export const getErrorMessage = (error: AxiosError<AxiosErrorData> | Error, showErrorData = true): string => {
  const defaultMessage = 'Error. Something went wrong';

  if (showErrorData) {
    if (axios.isAxiosError(error)) {
      if (error.response && error.response.data) {
        // let status = (`${error.response.status || ''} ${error.response.statusText || ''}`).trim();
        // status = status ? `${status}.` : status;

        if (error.response.data.message && typeof error.response.data.message === 'string') {
          return `${error.response.data.message || defaultMessage}`;
        }
        if (error.response.data.error && typeof error?.response?.data.error === 'string') {
          return `${error.response.data.error || defaultMessage}`;
        }
        if (typeof error.response.data === 'string') {
          return error.response.data;
        }
      }

      if (typeof error.request === 'string') {
        return error.request;
      }
    }

    if (typeof error.message === 'string') {
      return error.message;
    }
  }

  return defaultMessage;
};

export function getFullName<T extends { firstName: string | undefined; lastName: string | undefined }>({
  firstName,
  lastName,
}: T): string {
  return `${firstName || ''} ${lastName || ''}`.trim();
}
